/**
 * Check-ins on Today (wave w1023c L1, Victor 2026-10-23: "Check-ins move to
 * Today"; `(?) checkin-custom-on-today`).
 *
 * The family feed (`GET /api/polls/feed`, query key `['polls','feed']`) holds
 * two kinds of send. A check-in or a custom form send — anything minted from a
 * form definition — is a program obligation, so it is a task row on Today. A
 * quick poll stays a card in the Community feed. These are the pure halves of
 * that split, shared by `DashboardPage` and `CommunityPage` so the two pages
 * can never both show a send, or both drop it (`isFormSend`, lib/poll-display).
 */
import { formatPreset, type PresetOptions } from '@/i18n/intl';
import { isFormSend } from '@/lib/poll-display';
import type { PollFeedItem } from '@/types/polls';

/** The one key Community and Today share for the family feed (#2389). */
export const POLLS_FEED_QUERY_KEY = ['polls', 'feed'] as const;

/** Today's rows: open, unanswered form sends, soonest due first. A row is gone once `voted`. */
export function openCheckinTasks(items: readonly PollFeedItem[], now: Date = new Date()): PollFeedItem[] {
  return items
    .filter((it) => isFormSend(it.poll) && !it.voted && it.poll.status === 'open')
    .filter((it) => {
      const end = new Date(it.poll.end_at).getTime();
      return Number.isNaN(end) || end > now.getTime();
    })
    .sort((a, b) => new Date(a.poll.end_at).getTime() - new Date(b.poll.end_at).getTime());
}

/** The Community feed's polls, and the only ones Today's "TRF is asking you N questions" counts. */
export function quickPollItems(items: readonly PollFeedItem[]): PollFeedItem[] {
  return items.filter((it) => !isFormSend(it.poll));
}

/**
 * The day a send is due, in the READER's zone and language: "Tue, Oct 27".
 * `end_at` is an instant (the poll closes at that moment), so the day is the
 * reader's calendar day of that instant — never the UTC day, which reads a day
 * late in Los Angeles for an evening close. Null for a missing or bad date, so
 * the row says nothing rather than "Invalid Date".
 */
export function checkinDueDay(endAt: string | null | undefined, options: PresetOptions = {}): string | null {
  if (!endAt) return null;
  const d = new Date(endAt);
  if (Number.isNaN(d.getTime())) return null;
  return formatPreset(d, 'weekdayMonthDay', options);
}
